// The auth guards that upstream declares as ui-router state `data`, enforced here from
// routes.json.
//
// `authentication/router-hooks/` has two `$stateChangeStart` hooks that read
// `data.requiresMfaVerificationPendingAuth` / `data.requiresNonMfaVerificationPendingAuth`
// off the state being entered, cancel the transition, and send the user wherever
// `getNextStateBasedOnSession()` says instead. `requiresNonAuth` is declared alongside them.
//
// ⚠️ THE PORT DECLARED ALL THREE AND ENFORCED NONE, which is worse than not extracting them:
// the route table says the rule exists, so reading it gives the impression the rule is kept.
// Driven from routes.json for the same reason `useRouteChrome` is — the route table is the
// source, so a guard that changes upstream arrives as a regenerated artefact rather than as
// a screen that quietly stops guarding.
//
// ── WHY THIS FILE IS WRITTEN AROUND ONE INVARIANT ─────────────────────────────────────────
//
// Five review rounds found five different holes in an incremental version of this: the
// outlet rendered regardless of the guard; then it honoured a readiness flag cleared too
// late; then readiness keyed on a stale path; then an unreadable session was treated as
// "signed out"; then unguarded routes were made to wait on a call they do not need. Every
// fix was correct and every one left a new gap, because the rule was implicit and the code
// was a pile of special cases. So the rule is stated once and the rest derives from it:
//
//     A route renders only when the guards DECLARED for it are KNOWN to be
//     satisfied for THIS navigation.
//
// Read literally, that settles all five:
//
//   "declared for it"  — a route with no guards has nothing to know, so it renders
//                        immediately and never waits on the session at all. That matters most
//                        for `enable-cookies` and `out-of-date`, which exist FOR the case
//                        where the session cannot be established: making them wait on /user
//                        hides the screens that explain the failure, and a hung request
//                        (there is no timeout in the API wrapper) hides them forever.
//   "known"            — an unreadable session (5xx, offline) is not "signed out". It yields
//                        no verdict, and a guarded route stays closed with an error the
//                        caller can render and retry, rather than a permanently blank frame.
//   "this navigation"  — keyed on the LOCATION OBJECT, not the pathname and not
//                        `location.key`. A pathname key matches a stale verdict when the same
//                        path is re-entered. `location.key` looked like the fix and is not:
//                        it identifies a history ENTRY, so a back/forward restores the key
//                        that entry had before, and a verdict cached under it is reused for a
//                        navigation that has not been checked. React Router allocates a new
//                        location OBJECT for every navigation including POP, and keeps it
//                        stable across re-renders of that navigation — which is exactly the
//                        identity this needs.
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, type Location } from 'react-router-dom'
import { routes } from '@woo-ext/protocol'
import { ApiError, getUser, type ExtensionUser } from '@woo-ext/api'

export const SessionState = {
  Active: 'active',
  MfaPending: 'mfa-pending',
  None: 'none',
  /** The server did not answer. NOT the same as "answered: nobody is signed in". */
  Unknown: 'unknown',
} as const
export type SessionState = (typeof SessionState)[keyof typeof SessionState]

/**
 * ⚠️ SIGNED OUT IS A **403** ON THIS API, NOT A 401. Measured against the dev tenant:
 * `GET /chrome-extension-api/user` with no session answers `403 Access Denied` from Tomcat.
 * So "only a 401 means signed out" — the obvious rule — would classify every signed-out
 * visitor as `Unknown` and make `/auth/login` unreachable for exactly the people who need it.
 *
 * ⚠️ AND THE STATUS IS CHECKED BEFORE THE BODY. `ApiError.needsMfa` is deliberately
 * body-only, because careero's SPA reads it that way (§1.3) — so on its own it classifies a
 * 401 or a 5xx that happens to carry `mfaValidated: false` as pending MFA, and sends that
 * user to the validation screen. Pending MFA is specifically a 403 WITH the flag.
 */
export function classifySessionError(error: unknown): SessionState {
  if (!(error instanceof ApiError)) return SessionState.Unknown
  if (error.isForbidden) return error.needsMfa ? SessionState.MfaPending : SessionState.None
  if (error.isUnauthenticated) return SessionState.None
  return SessionState.Unknown
}

/**
 * The session AND the user it resolved, because the caller needs both.
 *
 * ⚠️ Returning only the state threw away the response and made every guarded route fetch
 * `/user` TWICE in sequence — once here, once when `StatusRoute` or `UserRoute` mounted —
 * for the same session, adding a round-trip of latency and doubling the tenant load on the
 * two screens that are hit most.
 */
export interface ResolvedSession {
  state: SessionState
  /** Present only for an Active session; the other states have no user to carry. */
  user?: ExtensionUser
}

/**
 * @param isUserBody The extension popup's rule (whitelabel-ext c18094b): a 200 whose body is not a
 *   user — a proxy or captive-portal page, which `safeJson` hands back as text — is UNREACHABLE,
 *   not signed in. The SPA passes nothing: its upstream accepts such a body as a session.
 */
export async function resolveSession(isUserBody?: (user: unknown) => boolean): Promise<ResolvedSession> {
  try {
    const user = await getUser()
    if (isUserBody !== undefined && !isUserBody(user)) return { state: SessionState.Unknown }
    return { state: SessionState.Active, user }
  } catch (error) {
    return { state: classifySessionError(error) }
  }
}

export async function resolveSessionState(): Promise<SessionState> {
  return (await resolveSession()).state
}

export interface RouteGuards {
  requiresNonAuth: boolean
  requiresMfaPending: boolean
  requiresNonMfaPending: boolean
}

export function guardsFor(pathname: string): RouteGuards {
  // Prefix matching, because /auth/mfa is the ABSTRACT state carrying
  // requiresMfaVerificationPendingAuth and ui-router's hook sees it on the state being
  // entered — so its children inherit it. Behaviour, not an implementation detail.
  const matches = routes.filter((route) => pathname === route.path || pathname.startsWith(`${route.path}/`))
  const has = (flag: string): boolean => matches.some((route) => route.data[flag] === true)
  return {
    requiresNonAuth: has('requiresNonAuth'),
    requiresMfaPending: has('requiresMfaVerificationPendingAuth'),
    requiresNonMfaPending: has('requiresNonMfaVerificationPendingAuth'),
  }
}

/** Whether this route declares anything at all. Unguarded routes never wait on the session. */
export function isGuarded(pathname: string): boolean {
  const guards = guardsFor(pathname)
  return guards.requiresNonAuth || guards.requiresMfaPending || guards.requiresNonMfaPending
}

/** `getNextStateBasedOnSession()`: where a user in this state belongs. */
export function nextPathFor(session: SessionState): string {
  if (session === SessionState.MfaPending) return '/chrome-extension/auth/mfa/validate'
  if (session === SessionState.Active) return '/chrome-extension/auth/status'
  return '/chrome-extension/auth/login'
}

function satisfied(guards: RouteGuards, session: SessionState): boolean {
  if (guards.requiresNonAuth && session !== SessionState.None) return false
  if (guards.requiresMfaPending && session !== SessionState.MfaPending) return false
  if (guards.requiresNonMfaPending && session === SessionState.MfaPending) return false
  return true
}

export type GuardVerdict =
  | { kind: 'allow'; session: SessionState }
  | { kind: 'redirect'; to: string }
  /** The session could not be read and this route needs it. Closed, with something to show. */
  | { kind: 'unavailable' }

/**
 * The whole decision, as one pure function — which is what makes it testable, and what stops
 * it drifting back into conditions spread across a component and an effect.
 */
/**
 * ⚠️ `requireSession` EXISTS BECAUSE `isGuarded` CANNOT SEE THE EXTENSION ROUTES' GUARD.
 *
 * The auth states declare their rules as the three `requires*` booleans, which survive
 * extraction as literals — so `guardsFor` reads them and `isGuarded` is true. The
 * `/extension/**` states declare `privAccess: UserRole.ExtensionUser` instead, an ENUM MEMBER
 * REFERENCE the extractor records as `<computed>`. So `isGuarded` returns false for every one
 * of them, `decide` allows on the first render with no request made, and the screens render
 * for a signed-out visitor.
 *
 * That was live for one commit, under a comment in `ExtensionLayout` asserting the opposite.
 * The caller states the requirement explicitly rather than the table implying it, because the
 * alternative — teaching the extractor to resolve enum references — would still leave
 * `extensionProduct: () => …` computed and would make this silently depend on a generator
 * change nobody reading the layout can see.
 */
export function decide(pathname: string, session: SessionState, requireSession = false): GuardVerdict {
  if (!requireSession && !isGuarded(pathname)) return { kind: 'allow', session }
  if (session === SessionState.Unknown) return { kind: 'unavailable' }
  if (requireSession && session !== SessionState.Active) {
    return { kind: 'redirect', to: nextPathFor(session) }
  }
  return satisfied(guardsFor(pathname), session)
    ? { kind: 'allow', session }
    : { kind: 'redirect', to: nextPathFor(session) }
}

export type AuthGuardState =
  /** Still resolving. A guarded route renders nothing; an unguarded one never reaches this. */
  | { status: 'resolving' }
  /** `user` is the one the guard already fetched — see `ResolvedSession`. */
  | { status: 'allowed'; session: SessionState; user?: ExtensionUser }
  /** Guarded, and the session could not be read. The caller shows an error with a retry. */
  | { status: 'unavailable'; retry: () => void }

/**
 * Resolves the session where the route needs it, and applies the declared guards.
 *
 * An unguarded route returns `allowed` on the FIRST render with no request made at all.
 */
export interface AuthGuardOptions {
  /** See `resolveSession` — the popup's "a non-user 200 is unreachable" rule. */
  isUserBody?: (user: unknown) => boolean
  /**
   * Resolve a session and require an ACTIVE one, whatever the route table says.
   *
   * For the `/extension/**` branch, whose `privAccess` does not survive extraction — see
   * `decide`.
   */
  requireSession?: boolean
}

/** Something that needs to know how a guarded navigation ended — see `onGuardVerdict`. */
export type GuardVerdictListener = (location: Location, verdict: GuardVerdict) => void

const verdictListeners = new Set<GuardVerdictListener>()

/**
 * Hear every verdict a mounted guard reaches, with the location it was reached for. Returns the
 * unsubscribe.
 *
 * ⚠️ THIS IS WHERE UPSTREAM'S `$stateChangeStart` REJECTION HAPPENS IN THIS PORT, and page
 * tracking needs it: ui-router refuses a guarded state before it is entered, so no
 * `$stateChangeSuccess` and no page view; here the location settles FIRST and the guard redirects
 * after, so a listener that tracked on the location alone recorded the protected page a
 * signed-out visitor never saw (measured: `/extension/shortlist/review` before `/auth/login`).
 */
export function onGuardVerdict(listener: GuardVerdictListener): () => void {
  verdictListeners.add(listener)
  return () => {
    verdictListeners.delete(listener)
  }
}

export function useAuthGuard({ requireSession = false, isUserBody }: AuthGuardOptions = {}): AuthGuardState {
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname } = location
  const guarded = requireSession || isGuarded(pathname)

  const [attempt, setAttempt] = useState(0)
  // The location OBJECT is the navigation's identity — see the note at the top of the file.
  const [resolved, setResolved] = useState<
    { location: Location; attempt: number; verdict: GuardVerdict; user?: ExtensionUser } | undefined
  >(undefined)

  useEffect(() => {
    if (!guarded) return undefined
    let live = true
    void resolveSession(isUserBody).then(({ state, user }) => {
      if (!live) return
      const verdict = decide(pathname, state, requireSession)
      for (const listener of verdictListeners) listener(location, verdict)
      if (verdict.kind === 'redirect') {
        navigate(verdict.to, { replace: true })
        return
      }
      setResolved({ location, attempt, verdict, user })
    })
    return () => {
      live = false
    }
  }, [guarded, pathname, location, attempt, navigate, requireSession])

  // Unguarded: nothing to know, so nothing to wait for. Decided during render, which is the
  // only way it can be true on the FIRST one.
  //
  // No user is carried here, and that is not an oversight: an unguarded route never resolved
  // a session, so there is nothing to carry. A route that needs the user fetches its own —
  // see `UserRoute`, where a fast path that assumed otherwise was dead code.
  if (!guarded) return { status: 'allowed', session: SessionState.Unknown }

  // A verdict from another navigation is not a verdict for this one — compared during render,
  // because an effect that invalidates it runs after the child has already mounted.
  const current =
    resolved !== undefined && resolved.location === location && resolved.attempt === attempt
      ? resolved.verdict
      : undefined

  if (current === undefined) return { status: 'resolving' }
  if (current.kind === 'allow') {
    return { status: 'allowed', session: current.session, user: resolved?.user }
  }
  // `redirect` is never stored — the effect navigates instead of resolving — so what is left
  // here is `unavailable`.
  return {
    status: 'unavailable',
    retry: () => {
      setAttempt((value) => value + 1)
    },
  }
}
